import type { CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { getCustomerCardBffBaseUrl } from '../config';
import { logCustomerCardError, logCustomerCardPageAction } from '../utils/newRelicLogging';
import { mapFormValuesToCreateConsumerPayload } from '../mappers/consumerMapper';
import { buildSwappedParticipants, swapParticipantRoles } from '../mappers/swapParticipantsMapper';
import type { CustomerCardStoreState } from '../types/CustomerCardTypes';
import { saveParticipantsRSAA } from './actionCreators';
import { saveFailureFacets } from './saveFailureFacets';
import { createConsumer } from './consumerActionCreators';
import {
    SWAP_BUYER_COBUYER_FAILURE,
    SWAP_BUYER_COBUYER_REQUEST,
    SWAP_BUYER_COBUYER_RESET,
    SWAP_BUYER_COBUYER_SUCCESS
} from './actions';
import * as customerCardSelectors from './selectors';

export type SwapResult = { ok: true } | { ok: false; error: string };

export const resetSwapStatus = () => ({ type: SWAP_BUYER_COBUYER_RESET });

type SwapThunkDispatch = (action: any) => any;
type SwapThunkGetState = () => CustomerCardStoreState;

const SWAP_FAILED = 'SwapBuyerCoBuyerFailed';
const CREATE_CONSUMER_FAILED = 'SwapCreateConsumerFailed';

export const SWAP_NR_ACTION = 'SwapBuyerCoBuyer';

export const swapBuyerCoBuyer =
    (buyer: IShopper, coBuyer: CoBuyer) =>
    async (dispatch: SwapThunkDispatch, getState: SwapThunkGetState): Promise<SwapResult> => {
        const state = getState();
        const dealId = customerCardSelectors.getDealId(state);

        const failed = (stage: string, facets: Record<string, unknown> = {}): SwapResult => {
            logCustomerCardPageAction(SWAP_NR_ACTION, { outcome: 'failure', stage, dealXgId: dealId, ...facets });
            dispatch({ type: SWAP_BUYER_COBUYER_FAILURE, payload: { error: stage } });
            return { ok: false, error: stage };
        };

        if (customerCardSelectors.getIsSaving(state)) {
            logCustomerCardError('SwapBlockedByCustomerCardSave', `A customer card save was already in flight for deal ${dealId}`);
            return failed('CustomerCardSaveInFlight');
        }

        const bffBaseUrl = getCustomerCardBffBaseUrl();
        if (!bffBaseUrl || !dealId) {
            return failed('MissingConfiguration');
        }

        try {
            dispatch({ type: SWAP_BUYER_COBUYER_REQUEST });

            let swappedBuyer = buyer;
            if (!buyer.commonConsumerId) {
                const stateTree = state as unknown as StateTree;
                const enableCorrectPhoneHandling =
                    featureToggleSelectors.areFeatureTogglesAvailable(stateTree) &&
                    featureToggleSelectors.isCorrectPhoneHandlingEnabled(stateTree);
                const created = await dispatch(
                    createConsumer(mapFormValuesToCreateConsumerPayload(buyer, enableCorrectPhoneHandling))
                );
                if (!created.ok) {
                    return failed(CREATE_CONSUMER_FAILED, { consumerErrorDetail: created.error });
                }
                swappedBuyer = { ...buyer, commonConsumerId: created.commonConsumerId };
            }

            const payload = buildSwappedParticipants(swappedBuyer, coBuyer, customerCardSelectors.getBridgeUsername(state));
            const dealerId = String(customerCardSelectors.getDealerId(state) ?? '');

            await dispatch(saveParticipantsRSAA(bffBaseUrl, dealId, dealerId, payload));

            const recordedError = customerCardSelectors.getSaveParticipantsError(getState());
            if (recordedError) {
                return failed(SWAP_FAILED, saveFailureFacets(recordedError));
            }

            const { nextBuyer, nextCoBuyer } = swapParticipantRoles(swappedBuyer, coBuyer);
            dispatch({
                type: SWAP_BUYER_COBUYER_SUCCESS,
                payload: { shopper: nextBuyer, coBuyer: nextCoBuyer }
            });

            return { ok: true };
        } catch (error) {
            logCustomerCardError(SWAP_FAILED, `Swap threw for deal ${dealId}: ${error}`);
            return failed(SWAP_FAILED);
        }
    };
